import { reactive } from "vue";
import { api } from "../api.js";

var defaults = {
	loading: false,
	error: null,
	data: null,
	lastFetched: null,
};

var timer = null;

var statusStore = {
	data: reactive({ ...defaults }),
	methods: {
		refresh: async function (opts) {
			statusStore.data.loading = true;
			try {
				var res = await api.get("/api/status", opts);
				statusStore.data.data = res;
				statusStore.data.lastFetched = Date.now();
				statusStore.data.error = null;
			} catch (e) {
				statusStore.data.error = e.message;
			} finally {
				statusStore.data.loading = false;
			}
		},
		startPolling: function (intervalMs) {
			statusStore.methods.stopPolling();
			statusStore.methods.refresh({ background: true });
			timer = setInterval(() => statusStore.methods.refresh({ background: true }), intervalMs ?? 10000);
		},
		stopPolling: function () {
			if (timer) { clearInterval(timer); timer = null; }
		},
		setSetpoint: async function (side, value, holdMinutes, options) {
			var body = { value, holdMinutes };
			if (options?.preserveMode) { body.preserveMode = true; }
			await api.put("/api/setpoint/" + side, body);
			await statusStore.methods.refresh();
		},
		setMode: async function (side, mode) {
			await api.put("/api/mode/" + side, { mode });
			await statusStore.methods.refresh();
		},
		releaseHold: async function (side) {
			var body = side ? { side } : {};
			await api.post("/api/actions/release", body);
			await statusStore.methods.refresh();
		},
		warm: async function (target, durationMinutes, side) {
			var body = { target, durationMinutes };
			if (side) { body.side = side; }
			await api.post("/api/actions/warm", body);
			await statusStore.methods.refresh();
		},
		cool: async function (target, durationMinutes, side) {
			var body = { target, durationMinutes };
			if (side) { body.side = side; }
			await api.post("/api/actions/cool", body);
			await statusStore.methods.refresh();
		},
		standby: async function (side) {
			var body = side ? { side } : {};
			await api.post("/api/actions/dock/stop", body);
			await statusStore.methods.refresh();
		},
		// Force the control loop to tick now (fresh SleepIQ + SleepMe + SmartThings
		// reads → new samples → /api/status reflects current state). Useful after
		// flipping the SleepIQ swap, getting into bed, or changing dock state from
		// the SleepMe app — instead of waiting up to loopIntervalSeconds.
		tickNow: async function () {
			await api.post("/api/actions/tick", {});
			await statusStore.methods.refresh();
		},
		stopDock: async function (side) {
			var body = side ? { side } : {};
			await api.post("/api/actions/dock/stop", body);
			await statusStore.methods.refresh();
		},
		startDock: async function (side) {
			var body = side ? { side } : {};
			await api.post("/api/actions/dock/start", body);
			await statusStore.methods.refresh();
		},
		sleepIn: async function (side, minutes) {
			var body = { minutes: minutes ?? 60 };
			if (side) { body.side = side; }
			await api.post("/api/actions/sleep-in", body);
			await statusStore.methods.refresh();
		},
		cancelSleepIn: async function (side) {
			var body = side ? { side } : {};
			await api.post("/api/actions/sleep-in/cancel", body);
			await statusStore.methods.refresh();
		},
		setRoomAdjust: async function (side, enabled) {
			var raw = statusStore.data.data?.config?.roomAdjustEnabled;
			var current;
			if (typeof raw === "boolean") {
				current = { left: raw, right: raw };
			} else {
				current = {
					left: raw?.left !== false,
					right: raw?.right !== false,
				};
			}
			current[side] = !!enabled;
			await api.put("/api/config", { roomAdjustEnabled: current });
			await statusStore.methods.refresh();
		},
		setPresenceRequired: async function (side, enabled) {
			var raw = statusStore.data.data?.config?.presenceRequiredToRun;
			var current;
			if (typeof raw === "boolean") {
				current = { left: raw, right: raw };
			} else {
				current = {
					left: raw?.left === true,
					right: raw?.right === true,
				};
			}
			current[side] = !!enabled;
			await api.put("/api/config", { presenceRequiredToRun: current });
			await statusStore.methods.refresh();
		},
		// Capture the current (empty-bed) SleepIQ chamber pressure as the baseline for
		// the pressure-fallback presence mode. Omit `side` to calibrate both. Returns
		// { captured, baseline, ... } so the caller can surface what was recorded.
		calibratePresence: async function (side) {
			var body = side ? { side } : {};
			var res = await api.post("/api/actions/calibrate-presence", body);
			await statusStore.methods.refresh();
			return res;
		},
		clearStore: function () {
			Object.assign(statusStore.data, defaults);
		},
	},
};

export default statusStore;
